<script lang="ts" setup>
import { defineProps, defineEmits, PropType } from "vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { WorkerReliability } from "@/types/WorkerReliability";
import ExternalLink from "@/const/externalLink";

defineProps({
  currentWorkerReliability: {
    type: Object as PropType<WorkerReliability>,
    required: true
  },
  noCloseOnBackdrop: {
    type: Boolean,
    default: false
  },
  noCloseOnEsc: {
    type: Boolean,
    default: false
  }
});

const emits = defineEmits(["click-confirm", "click-close"]);

const ok = () => {
  emits("click-confirm");
};

const close = () => {
  emits("click-close");
};
</script>

<template>
  <BModal
    id="confirm-cancel-due-to-infection"
    centered
    dialog-class="confirm-cancel-modal dialog"
    content-class="confirm-cancel-modal content try-cancel-modal"
    body-class="confirm-cancel-modal body"
    header-class="confirm-cancel-modal header-no-penalty suspend-info"
    :no-close-on-backdrop="noCloseOnBackdrop"
    :no-close-on-esc="noCloseOnEsc"
    @ok="ok"
    @close="close"
  >
    <template #modal-header="{ close }">
      <div class="header-content">
        ペナルティは発生しません
      </div>
      <span class="header-close">
        <FeatherIcon icon="x-circle" size="24" @click="close" />
      </span>
    </template>
    <div class="body">
      <p class="content-description">
        事業所で発生している感染症が理由のため、ペナルティは発生しません
      </p>
      <div v-if="currentWorkerReliability" class="content-body-container">
        <div class="content-body">
          <p class="content-body--changed-label">信用スコア</p>
          <p class="content-body--changed-body">
            <span class="current">
              {{ currentWorkerReliability.creditScore }} pt</span
            >
            →
            <span class="current">
              {{ currentWorkerReliability.creditScore }} pt</span
            >
          </p>
        </div>
        <div class="content-body">
          <p class="content-body--changed-label">最大応募可能件数</p>
          <p class="content-body--changed-body">
            <span class="current">
              {{ currentWorkerReliability.applyLimit }} 件</span
            >
            →
            <span class="current">
              {{ currentWorkerReliability.applyLimit }} 件</span
            >
          </p>
        </div>
      </div>
      <p class="content-caution">
        信用スコアが<span class="content-caution notion">0pt</span
        >になった場合、<span class="content-caution notion"
          >応募済みのお仕事は全て自動でキャンセル</span
        >となり、<span class="content-caution notion"
          >一時的にお仕事へ応募不可</span
        >となります。<br />
        詳細は<a
          :href="ExternalLink.CREDIT_SCORE_IS_ZERO_FAQ_URL"
          target="_blank"
          class="text-primary"
          >こちら</a
        >をご覧ください。
      </p>
    </div>
    <template #modal-footer="{ close, ok }">
      <div class="footer-actions">
        <BButton
          block
          variant="outline-gray-04 text-primary-dark"
          @click="close"
          class="footer-actions button"
          >戻る</BButton
        >
        <div class="footer-actions divider" />
        <BButton
          block
          variant="blue text-white"
          @click="ok"
          class="footer-actions button"
          >了承する
        </BButton>
      </div>
    </template>
  </BModal>
</template>

<style lang="scss" scoped>
.try-cancel-modal {
  & .header-content {
    width: 100%;
    font-family: "Noto Sans JP", sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: $white;
    text-align: center;
  }

  & .header-close {
    position: absolute;
    top: -12px;
    right: -12px;
    line-height: 0;
    cursor: pointer;

    svg {
      color: $white;
      fill: $white;

      :deep(line) {
        color: $gray-600;
      }
    }
  }

  & .body {
    .content-description {
      font-family: "Noto Sans JP", sans-serif;
      font-size: 16px;
      font-weight: 700;
    }

    .content-caution {
      font-family: "Noto Sans JP", sans-serif;
      font-size: 12px;
      font-weight: 400;

      &.notion {
        color: $red;
      }
    }

    .content-body {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 5px 10px;
      font-size: 17px;
      background-color: #fffded;
      border: 1px solid #efb30e;
      border-radius: 4px;

      &:first-child {
        margin-bottom: 5px;
      }

      &-container {
        margin: 10px 0;
      }
    }

    .content-body--policy-link {
      color: $blue;
    }

    .content-body--changed-label {
      font-family: "Noto Sans JP", sans-serif;
      font-size: 12px;
      font-weight: 700;
    }

    .content-body--changed-body {
      font-family: "Open Sans", sans-serif;
      font-size: 16px;
      font-weight: 400;
      text-align: center;
    }

    .current {
      color: $gray-700;
    }
  }

  & .footer-actions {
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
    height: 40px;

    &.divider {
      width: 24px;
    }

    &.button {
      font-family: "Noto Sans JP", sans-serif;
      font-size: 16px;
      font-weight: 700;
    }
  }
}
</style>
